import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BookmarkPlus, Loader2, Lock, Sparkles } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { SegmentedTabs } from 'components/ui/data-table';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { useToast } from 'components/ui/use-toast';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { useIsMobile } from 'hooks/use-media-query';
import {
  VOCAB_QUERY_KEY,
  defineVocabWords,
  deleteVocabWord,
  downloadVocabCsv,
  saveVocabWord,
  setVocabLearned,
  useVocabulary,
  vocabErrorCode,
  type VocabWord,
} from 'modules/vocabulary';
import { WordLists, type ListWord } from 'modules/wordLists';
import { PortalHeading } from './PortalHeading';
import { WordCard } from './words/WordCard';
import { filterWords, learnedCount, type SourceFilter, type StatusFilter } from './words/filterWords';
import { WordsFilters } from './words/WordsFilters';
import { WordRow, WORDS_GRID } from './words/WordRow';
import { WordsExport } from './words/WordsExport';

type WordsView = 'mine' | 'lists';

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** "Mening so'zlarim": words saved from IELTS/SAT passages. Definitions and the download come with a plan. */
export default function PortalWords() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useVocabulary();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [busy, setBusy] = useState<'define' | 'download' | null>(null);
  // The view is in the URL, so a shared or reloaded link opens the same tab.
  const [params, setParams] = useSearchParams();
  const view: WordsView = params.get('view') === 'lists' ? 'lists' : 'mine';
  const savedWords = useMemo(() => new Set((data?.words ?? []).map((w) => w.word.toLowerCase())), [data]);

  const locked = data ? !data.access : false;
  const shown = useMemo(
    () => filterWords(data?.words ?? [], { query, status, source, language: i18n.language }),
    [data, query, status, source, i18n.language],
  );
  const learned = learnedCount(data?.words ?? []);
  const undefinedCount = data?.words.filter((w) => !w.definition).length ?? 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: VOCAB_QUERY_KEY });

  const run = async (kind: 'define' | 'download') => {
    if (locked) {
      setUpgradeOpen(true);
      return;
    }
    setBusy(kind);
    try {
      if (kind === 'define') {
        // Twenty words per call: keep going until every saved word has a definition.
        let remaining = undefinedCount;
        while (remaining > 0) {
          const result = await defineVocabWords();
          remaining = result.defined > 0 ? result.remaining : 0;
        }
        await refresh();
      } else {
        saveBlob(await downloadVocabCsv(), 'my-words.csv');
      }
    } catch (error) {
      if (vocabErrorCode(error) === 'PORTAL_PLAN_REQUIRED') setUpgradeOpen(true);
      else toast({ title: t(kind === 'define' ? 'vocab.define_failed' : 'vocab.download_failed'), variant: 'destructive' });
      await refresh();
    } finally {
      setBusy(null);
    }
  };

  const saveListWord = async (item: ListWord) => {
    try {
      await saveVocabWord(item.word, item.example);
      toast({ title: t('vocab.saved', { word: item.word }), variant: 'success' });
      await refresh();
    } catch {
      toast({ title: t('vocab.save_failed'), variant: 'destructive' });
    }
  };

  const toggleLearned = async (item: VocabWord) => {
    try {
      await setVocabLearned(item.id, !item.learned);
      await refresh();
    } catch {
      toast({ title: t('vocab.learn_failed'), variant: 'destructive' });
    }
  };

  const remove = async (item: VocabWord) => {
    try {
      await deleteVocabWord(item.id);
      await refresh();
    } catch {
      toast({ title: t('vocab.delete_failed'), variant: 'destructive' });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/portal/settings"
        className="inline-flex min-h-11 items-center gap-1 self-start text-[13px] font-bold text-muted-foreground hover:text-foreground md:hidden"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> {t('portal.account')}
      </Link>
      <PortalHeading
        title={t('vocab.title')}
        description={data?.words.length ? `${t('vocab.subtitle')} ${t('vocab.counts', { count: data.words.length, learned })}` : t('vocab.subtitle')}
        aside={
          view === 'mine' && data?.words.length ? (
            <div className="flex shrink-0 flex-wrap gap-2 max-md:flex-col">
              {undefinedCount > 0 && (
                <Button type="button" variant="outline" onClick={() => run('define')} disabled={busy !== null}>
                  {busy === 'define' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : locked ? <Lock className="mr-2 h-4 w-4" aria-hidden /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden />}
                  {t('vocab.define', { count: undefinedCount })}
                </Button>
              )}
              <div className="flex gap-2 max-md:[&>*]:flex-1">
                <Button variant="outline" className="max-md:h-11" asChild>
                  <Link to="/portal/words/cards">{t(isMobile ? 'flashcards.link_short' : 'flashcards.link')}</Link>
                </Button>
                <WordsExport
                  unlocked={!locked}
                  busy={busy === 'download'}
                  words={data.words}
                  onDownload={() => void run('download')}
                  onUpgrade={() => setUpgradeOpen(true)}
                />
              </div>
            </div>
          ) : null
        }
      />

      <SegmentedTabs<WordsView>
        aria-label={t('vocab.title')}
        className="flex h-10 w-full self-start md:inline-flex md:w-auto [&>button]:flex-1 [&>button]:justify-center md:[&>button]:flex-none"
        options={[
          { value: 'mine' as const, label: t('wordLists.tab_mine') },
          { value: 'lists' as const, label: t('wordLists.tab_lists') },
        ]}
        value={view}
        onChange={(next) => setParams(next === 'lists' ? { view: 'lists' } : {}, { replace: true })}
      />

      {view === 'lists' ? (
        <WordLists savedWords={savedWords} onSave={saveListWord} />
      ) : (
      <>
      {locked && data && data.words.length > 0 && (
        <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">{t('vocab.plan_hint')}</p>
      )}

      {view === 'mine' && data && data.words.length > 0 && (
        <WordsFilters
          query={query}
          onQuery={setQuery}
          status={status}
          onStatus={setStatus}
          source={source}
          onSource={setSource}
          total={data.words.length}
          learned={learned}
        />
      )}

      <section
        aria-label={t('vocab.title')}
        className={isMobile && data?.words.length ? '' : 'overflow-hidden rounded-[22px] border border-border bg-card'}
      >
        {isLoading ? (
          <div className="space-y-3 p-5" aria-hidden="true">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : isError ? (
          <p className="p-5 text-sm text-destructive">{t('vocab.load_failed')}</p>
        ) : !data?.words.length ? (
          <EmptyState icon={<BookmarkPlus className="h-5 w-5" />} title={t('vocab.empty_title')} description={t('vocab.empty_body')} />
        ) : isMobile ? (
          shown.length ? (
            <ul className="flex flex-col gap-2.5">
              {shown.map((item) => (
                <WordCard key={item.id} item={item} locked={locked} onDelete={() => void remove(item)} onToggleLearned={() => void toggleLearned(item)} />
              ))}
            </ul>
          ) : (
            <p className="rounded-[18px] border border-border bg-card p-5 text-center text-sm text-muted-foreground">{t('vocab.no_matches')}</p>
          )
        ) : (
          <>
          <div aria-hidden="true" className={`hidden border-b border-border bg-muted/40 px-[22px] py-3.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground ${WORDS_GRID}`}>
            <span>{t('vocab.col_word')}</span>
            <span>{t('vocab.col_translation')}</span>
            <span>{t('vocab.col_example')}</span>
            <span>{t('vocab.col_source')}</span>
            <span>{t('vocab.col_status')}</span>
            <span />
          </div>
          {shown.length ? (
            <ul className="divide-y divide-border">
              {shown.map((item) => (
                <WordRow key={item.id} item={item} locked={locked} onDelete={() => void remove(item)} onToggleLearned={() => void toggleLearned(item)} />
              ))}
            </ul>
          ) : (
            <p className="p-8 text-center text-sm text-muted-foreground">{t('vocab.no_matches')}</p>
          )}
          </>
        )}
      </section>
      </>
      )}

      <PlanCheckoutModal open={upgradeOpen} onOpenChange={setUpgradeOpen} defaultPlan="PRO" />
    </div>
  );
}
